import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ConfirmProvider } from "@/components/ConfirmDialog";import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import PortalLayout from "@/components/PortalLayout";
import OwnerPortalLayout from "@/components/OwnerPortalLayout";
import { Toaster } from "@/components/ui/sonner";
import { userMatchesPortal, landingForUser, urlForPortal } from "@/lib/portal";

import Login from "@/pages/Login";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Contacts from "@/pages/Contacts";
import ContactDetail from "@/pages/ContactDetail";
import Properties from "@/pages/Properties";
import PropertyDetail from "@/pages/PropertyDetail";
import UnitDetail from "@/pages/UnitDetail";
import Tenancies from "@/pages/Tenancies";
import TenancyDetail from "@/pages/TenancyDetail";
import { MisdirectPromptHost } from "@/components/bank/MisdirectPrompt";
import Documents from "@/pages/Documents";
import Tasks from "@/pages/Tasks";
import Users from "@/pages/Users";
import Search from "@/pages/Search";
import Tickets from "@/pages/Tickets";
import BankPage from "@/pages/Bank";
import PayoutOrders from "@/pages/PayoutOrders";
import BankAssistent from "@/pages/BankAssistent";
import Abschlaege from "@/pages/Abschlaege";
import Invoices from "@/pages/Invoices";
import Kaution from "@/pages/Kaution";
import Schaeden from "@/pages/Schaeden";
import SchadenDetail from "@/pages/SchadenDetail";
import KautionMagic from "@/pages/KautionMagic";
import WEG from "@/pages/WEG";
import WegKonten from "@/pages/WegKonten";
import WirtschaftsplanDetail from "@/pages/WirtschaftsplanDetail";
import AssemblyDetail from "@/pages/AssemblyDetail";
import AssemblyPresentation from "@/pages/AssemblyPresentation";
import Settings from "@/pages/Settings";
import Archiv from "@/pages/Archiv";
import Vorgaenge from "@/pages/Vorgaenge";
import Postfach from "@/pages/Postfach";
import Mieterhoehungen from "@/pages/Mieterhoehungen";
import Rueckantwort from "@/pages/Rueckantwort";
import MieterhoehungAssistent from "@/pages/MieterhoehungAssistent";
import Vergleichsmieten from "@/pages/Vergleichsmieten";
import Entwuerfe from "@/pages/Entwuerfe";
import BewerbungForm from "@/pages/BewerbungForm";
import BewerbungStep from "@/pages/BewerbungStep";
import HandyScan from "@/pages/HandyScan";
import InteressentenstammSelfService from "@/pages/InteressentenstammSelfService";
import Vermarktung, { VermarktungBewerber } from "@/pages/Vermarktung";
import BankInbox from "@/pages/BankInbox";
import Oelstand from "@/pages/public/Oelstand";
import Besichtigungen from "@/pages/Besichtigungen";
import Uebergabetermine from "@/pages/Uebergabetermine";
import InseratDetail from "@/pages/InseratDetail";
import { AngeboteListe, AngebotDetail, EinladungAntwort } from "@/pages/public/Angebote";
import Rollen from "@/pages/Rollen";
import Stammdaten from "@/pages/Stammdaten";
import LetterVerify from "@/pages/LetterVerify";
import MeterScanner from "@/pages/MeterScanner";
import Zaehler from "@/pages/Zaehler";
import OwnerReport from "@/pages/OwnerReport";
import SteuerberaterReport from "@/pages/SteuerberaterReport";
import Nebenkosten from "@/pages/Nebenkosten";
import NebenkostenDetail from "@/pages/NebenkostenDetail";
import Betriebskosten from "@/pages/Betriebskosten";
import BetriebskostenDetail from "@/pages/BetriebskostenDetail";
import WegJahresabrechnung from "@/pages/WegJahresabrechnung";
import Anfechtungen from "@/pages/Anfechtungen";
import BelegPruefungen from "@/pages/BelegPruefungen";
import Analytics from "@/pages/Analytics";
import CostPositions from "@/pages/CostPositions";
import ManagementContracts from "@/pages/ManagementContracts";
import Kostenblatt from "@/pages/Kostenblatt";
import Contracts from "@/pages/Contracts";
import Protocols from "@/pages/Protocols";
import DocumentTemplates from "@/pages/DocumentTemplates";
import DocumentTemplateEditor from "@/pages/DocumentTemplateEditor";
import ContractTemplates from "@/pages/ContractTemplates";
import ContractTemplateEditor from "@/pages/ContractTemplateEditor";
import EmailTemplates from "@/pages/EmailTemplates";
import MeinProfil from "@/pages/MeinProfil";
import AmtlicheDokumente from "@/pages/AmtlicheDokumente";
import EigentuemerMappe from "@/pages/EigentuemerMappe";
import Zahlungsaufforderungen from "@/pages/Zahlungsaufforderungen";
import ZahlungsaufforderungsVorlagen from "@/pages/ZahlungsaufforderungsVorlagen";
import MahngebuehrenReport from "@/pages/MahngebuehrenReport";
import SegAnalysen from "@/pages/SegAnalysen";
import SnippetsPage from "@/pages/Snippets";
import PortalSetup from "@/pages/PortalSetup";
import { PortalHome } from "@/pages/portal/PortalHome";
import { PortalTickets } from "@/pages/portal/PortalTickets";
import { PortalLedger, PortalDocs, PortalProfile } from "@/pages/portal/PortalLedger";
import PortalDokumente from "@/pages/portal/PortalDokumente";
import Mandanten from "@/pages/Mandanten";
import MandantenDetail from "@/pages/MandantenDetail";
import {
  OwnerHome, OwnerProperties, OwnerPropertyDetail, OwnerIncome, OwnerDocs, OwnerProfile,
  OwnerCosts, OwnerCalendar, OwnerContracts,
} from "@/pages/owner/OwnerPages";
import OwnerBeschluesse from "@/pages/owner/OwnerBeschluesse";
import OwnerHausgeld from "@/pages/owner/OwnerHausgeld";
import OwnerVersammlungen from "@/pages/owner/OwnerVersammlungen";
import OwnerJahresabrechnung from "@/pages/owner/OwnerJahresabrechnung";

/**
 * If a user is in the wrong portal (subdomain) for their role,
 * show a friendly notice + offer the correct portal URL.
 */
function PortalGuard({ children }) {
  const { user } = useAuth();
  if (user === null) return null;
  if (!user) return children;

  if (!userMatchesPortal(user)) {
    // determine right portal for this user
    const right =
      user.role === "platform_admin" ? "admin" :
      user.role === "owner" ? "eigentuemer" :
      user.role === "tenant" ? "portal" : "app";
    const rightUrl = urlForPortal(right);

    // Auto-redirect: if rightUrl is on the SAME hostname (preview env with ?portal=...),
    // we can just push the new query and the SPA stays mounted.
    try {
      const target = new URL(rightUrl, window.location.href);
      if (target.host === window.location.host) {
        window.location.replace(target.pathname + target.search + target.hash);
        return null;
      }
    } catch { /* ignore */ }

    // Otherwise show a friendly card (different hostname → user has to switch domains)
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-md p-6 shadow-sm text-center">
          <img src="/logo.svg" alt="Domivus" className="w-12 h-12 mx-auto mb-3" />
          <div className="text-base font-semibold text-slate-900">Anderes Portal</div>
          <p className="text-sm text-slate-600 mt-2">
            Ihr Konto wird im Portal <b>{right}</b> verwendet. Wir leiten Sie automatisch dorthin weiter.
          </p>
          <a
            href={rightUrl}
            className="inline-block mt-4 px-4 py-2 bg-slate-900 text-white text-sm rounded-sm hover:bg-slate-800"
            data-testid="portal-switch-link"
          >
            Zum richtigen Portal wechseln
          </a>
        </div>
      </div>
    );
  }
  return children;
}

function HomeRouter() {
  const { user } = useAuth();
  if (user === null) return null;
  if (!user) return <Navigate to="/login" replace />;
  const target = landingForUser(user);
  if (target !== "/") return <Navigate to={target} replace />;
  return <Dashboard />;
}

// Staff gate – redirects non-staff to their correct portal
function StaffOrPortalGate() {
  const { user } = useAuth();
  if (user && user.role === "platform_admin") return <Navigate to="/mandanten" replace />;
  if (user && user.role === "tenant") return <Navigate to="/portal" replace />;
  if (user && user.role === "owner") return <Navigate to="/eigentuemer" replace />;
  return <Layout />;
}

function App() {
  return (
    <AuthProvider>
      <ConfirmProvider>
        <BrowserRouter>
        <PortalGuard>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/portal/setup/:token" element={<PortalSetup />} />
            {/* Öffentliches Bewerbungsformular Stufe 1 (kein Login) */}
            <Route path="/bewerbung/:tenantSlug/:listingSlug" element={<BewerbungForm />} />
            <Route path="/bewerbung/:tenantSlug/step/:token" element={<BewerbungStep />} />
            <Route path="/handy-scan/:token" element={<HandyScan />} />
            <Route path="/angebote/:tenantSlug" element={<AngeboteListe />} />
            <Route path="/angebote/:tenantSlug/:slug" element={<AngebotDetail />} />
            <Route path="/einladung/:token" element={<EinladungAntwort />} />
            <Route path="/rueckantwort/:token" element={<Rueckantwort />} />
            <Route path="/oelstand/:token" element={<Oelstand />} />
            <Route path="/interessentenstamm" element={<InteressentenstammSelfService />} />
            {/* iter139 — Öffentliche Kautions-Freigabe via Magic-Link */}
            <Route path="/kaution-freigabe/:token" element={<KautionMagic />} />

            {/* 2026-03 — Öffentliche Dokument-Prüfseite (ohne Login).
                Die interne Staff-Prüfseite bleibt unter /app/dokument-pruefen
                zusätzlich verfügbar; hier ist die Empfänger-Route. */}
            <Route path="/dokument-pruefen" element={<LetterVerify />} />

            {/* 2026-03 — Mobiler Zähler-Scanner (nutzt Kamera, keine Sidebar) */}
            <Route path="/zaehler/erfassen" element={<MeterScanner />} />

            {/* Mandantenzentrale (platform_admin only) */}
            <Route path="/mandanten" element={
              <ProtectedRoute roles={["platform_admin"]}><Mandanten /></ProtectedRoute>
            } />
            <Route path="/mandanten/:id" element={
              <ProtectedRoute roles={["platform_admin"]}><MandantenDetail /></ProtectedRoute>
            } />

            {/* Tenant portal */}
            <Route path="/portal" element={
              <ProtectedRoute roles={["tenant"]}><PortalLayout /></ProtectedRoute>
            }>
              <Route index element={<PortalHome />} />
              <Route path="tickets" element={<PortalTickets />} />
              <Route path="mietkonto" element={<PortalLedger />} />
              <Route path="dokumente" element={<PortalDocs />} />
              <Route path="meine-dokumente" element={<PortalDokumente />} />
              <Route path="profil" element={<PortalProfile />} />
            </Route>

            {/* Owner portal */}
            <Route path="/eigentuemer" element={
              <ProtectedRoute roles={["owner"]}><OwnerPortalLayout /></ProtectedRoute>
            }>
              <Route index element={<OwnerHome />} />
              <Route path="kosten" element={<OwnerCosts />} />
              <Route path="kalender" element={<OwnerCalendar />} />
              <Route path="vertraege" element={<OwnerContracts />} />
              <Route path="beschluesse" element={<OwnerBeschluesse />} />
              <Route path="versammlungen" element={<OwnerVersammlungen />} />
              <Route path="hausgeld" element={<OwnerHausgeld />} />
              <Route path="jahresabrechnung" element={<OwnerJahresabrechnung />} />
              <Route path="objekte" element={<OwnerProperties />} />
              <Route path="objekte/:id" element={<OwnerPropertyDetail />} />
              <Route path="einnahmen" element={<OwnerIncome />} />
              <Route path="dokumente" element={<OwnerDocs />} />
              <Route path="meine-dokumente" element={<PortalDokumente />} />
              <Route path="profil" element={<OwnerProfile />} />
            </Route>

            {/* Staff app */}
            <Route path="/" element={
              <ProtectedRoute><StaffOrPortalGate /></ProtectedRoute>
            }>
              <Route index element={<HomeRouter />} />
              <Route path="kontakte" element={<Contacts />} />
              <Route path="kontakte/:id" element={<ContactDetail />} />
              <Route path="objekte" element={<Properties />} />
              <Route path="objekte/:id" element={<PropertyDetail />} />
              <Route path="objekte/:propertyId/einheiten/:unitId" element={<UnitDetail />} />
              <Route path="mietverhaeltnisse" element={<Tenancies />} />
              <Route path="mietverhaeltnisse/:id" element={<TenancyDetail />} />
              <Route path="dokumente" element={<Documents />} />
              <Route path="aufgaben" element={<Tasks />} />
              <Route path="tickets" element={<Tickets />} />
              <Route path="bank" element={<BankPage />} />
              <Route path="zahlungsauftraege" element={<PayoutOrders />} />
              <Route path="bank/assistent" element={<BankAssistent />} />
              <Route path="bank/archiv" element={<Navigate to="/archiv?tab=bank" replace />} />
              <Route path="abschlaege" element={<Abschlaege />} />
              <Route path="rechnungen" element={
                <ProtectedRoute roles={["super_admin", "admin", "management", "manager", "accountant"]}><Invoices /></ProtectedRoute>
              } />
              <Route path="kaution" element={
                <ProtectedRoute roles={["super_admin", "admin", "management", "manager", "accountant"]}><Kaution /></ProtectedRoute>
              } />
              <Route path="schaeden" element={
                <ProtectedRoute roles={["super_admin", "admin", "management", "manager", "accountant"]}><Schaeden /></ProtectedRoute>
              } />
              <Route path="schaden/:id" element={
                <ProtectedRoute roles={["super_admin", "admin", "management", "manager", "accountant"]}><SchadenDetail /></ProtectedRoute>
              } />
              <Route path="zaehler" element={
                <ProtectedRoute roles={["super_admin", "admin", "management", "manager", "accountant"]}><Zaehler /></ProtectedRoute>
              } />
              <Route path="weg" element={<WEG />} />
              <Route path="weg/konten" element={<WegKonten />} />
              <Route path="weg/wirtschaftsplan/:planId" element={<WirtschaftsplanDetail />} />
              <Route path="weg/versammlung/:assemblyId" element={<AssemblyDetail />} />
              <Route path="weg/versammlung/:assemblyId/praesentation" element={<AssemblyPresentation />} />
              <Route path="auswertungen" element={<Analytics />} />
              <Route path="kosten" element={
                <ProtectedRoute roles={["super_admin", "admin", "management", "manager", "accountant"]}><CostPositions /></ProtectedRoute>
              } />
              <Route path="verwaltervertraege" element={
                <ProtectedRoute roles={["super_admin", "admin", "management", "manager"]}><ManagementContracts /></ProtectedRoute>
              } />
              <Route path="kostenblatt" element={
                <ProtectedRoute roles={["super_admin", "admin", "management", "manager"]}><Kostenblatt /></ProtectedRoute>
              } />
              <Route path="vertraege" element={
                <ProtectedRoute roles={["super_admin", "admin", "management", "manager"]}><Contracts /></ProtectedRoute>
              } />
              <Route path="protokolle" element={
                <ProtectedRoute roles={["super_admin", "admin", "management", "manager"]}><Protocols /></ProtectedRoute>
              } />
              <Route path="mahnwesen" element={
                <Navigate to="/zahlungsaufforderungen" replace />
              } />
              <Route path="nebenkosten" element={
                <ProtectedRoute roles={["super_admin", "admin", "management", "manager", "accountant"]}><Nebenkosten /></ProtectedRoute>
              } />
              <Route path="nebenkosten/:id" element={
                <ProtectedRoute roles={["super_admin", "admin", "management", "manager", "accountant"]}><NebenkostenDetail /></ProtectedRoute>
              } />
              <Route path="betriebskosten" element={
                <ProtectedRoute roles={["super_admin", "admin", "management", "manager", "accountant"]}><Betriebskosten /></ProtectedRoute>
              } />
              <Route path="betriebskosten/:id" element={
                <ProtectedRoute roles={["super_admin", "admin", "management", "manager", "accountant"]}><BetriebskostenDetail /></ProtectedRoute>
              } />
              <Route path="weg-jahresabrechnung" element={
                <ProtectedRoute roles={["super_admin", "admin", "management", "manager", "accountant"]}><WegJahresabrechnung /></ProtectedRoute>
              } />
              <Route path="anfechtungen" element={
                <ProtectedRoute roles={["super_admin", "admin", "management", "manager", "accountant"]}><Anfechtungen /></ProtectedRoute>
              } />
              <Route path="beleg-pruefungen" element={
                <ProtectedRoute roles={["super_admin", "admin", "management", "manager", "accountant"]}><BelegPruefungen /></ProtectedRoute>
              } />
              <Route path="einstellungen" element={
                <ProtectedRoute roles={["super_admin", "admin"]}><Settings /></ProtectedRoute>
              } />
              <Route path="stammdaten" element={
                <ProtectedRoute roles={["super_admin", "admin"]}><Stammdaten /></ProtectedRoute>
              } />
              <Route path="dokument-pruefen" element={
                <ProtectedRoute roles={["super_admin", "admin", "management", "manager", "accountant"]}><LetterVerify /></ProtectedRoute>
              } />
              <Route path="eigentumerreport" element={
                <ProtectedRoute roles={["super_admin", "admin", "management", "manager", "accountant"]}><OwnerReport /></ProtectedRoute>
              } />
              <Route path="steuerberater-auswertung" element={
                <ProtectedRoute roles={["super_admin", "admin", "management", "manager", "accountant"]}><SteuerberaterReport /></ProtectedRoute>
              } />
              <Route path="benutzer" element={
                <ProtectedRoute roles={["super_admin", "admin"]}><Users /></ProtectedRoute>
              } />
              <Route path="archiv" element={
                <ProtectedRoute roles={["super_admin", "admin", "management", "manager"]}><Archiv /></ProtectedRoute>
              } />
              <Route path="vorgaenge" element={<Vorgaenge />} />
              <Route path="postfach" element={<Postfach />} />
              <Route path="mieterhoehungen" element={<Mieterhoehungen />} />
              <Route path="mieterhoehungen/assistent" element={<MieterhoehungAssistent />} />
              <Route path="vergleichsmieten" element={<Vergleichsmieten />} />
              <Route path="entwuerfe" element={<Entwuerfe />} />
              <Route path="vermarktung" element={<Vermarktung />} />
              <Route path="vermarktung/bewerber/:id" element={<VermarktungBewerber />} />
              <Route path="bank/inbox" element={<BankInbox />} />
              <Route path="besichtigungen" element={<Besichtigungen />} />
              <Route path="uebergabetermine" element={<Uebergabetermine />} />
              <Route path="vermarktung/inserat/:id" element={<InseratDetail />} />
              <Route path="rollen" element={<Rollen />} />
              <Route path="vorlagen" element={
                <ProtectedRoute roles={["super_admin", "admin", "management", "manager"]}><DocumentTemplates /></ProtectedRoute>
              } />
              <Route path="vorlagen/:id/bearbeiten" element={
                <ProtectedRoute roles={["super_admin", "admin", "management", "manager"]}><DocumentTemplateEditor /></ProtectedRoute>
              } />
              <Route path="vertragsvorlagen" element={
                <ProtectedRoute roles={["super_admin", "admin", "management", "manager"]}><ContractTemplates /></ProtectedRoute>
              } />
              <Route path="vertragsvorlagen/:id/bearbeiten" element={
                <ProtectedRoute roles={["super_admin", "admin", "management", "manager"]}><ContractTemplateEditor /></ProtectedRoute>
              } />
              <Route path="email-vorlagen" element={
                <ProtectedRoute roles={["super_admin", "admin", "management", "manager"]}><EmailTemplates /></ProtectedRoute>
              } />
              <Route path="mein-profil" element={
                <ProtectedRoute><MeinProfil /></ProtectedRoute>
              } />
              <Route path="amtliche-dokumente" element={
                <ProtectedRoute><AmtlicheDokumente /></ProtectedRoute>
              } />
              <Route path="eigentuemer/:id/mappe" element={
                <ProtectedRoute><EigentuemerMappe /></ProtectedRoute>
              } />
              <Route path="zahlungsaufforderungen" element={
                <ProtectedRoute><Zahlungsaufforderungen /></ProtectedRoute>
              } />
              <Route path="briefvorlagen/zahlungsaufforderungen" element={
                <ProtectedRoute roles={["super_admin", "admin", "management", "manager"]}>
                  <ZahlungsaufforderungsVorlagen />
                </ProtectedRoute>
              } />
              <Route path="finanzen/steuer/mahngebuehren" element={
                <ProtectedRoute><MahngebuehrenReport /></ProtectedRoute>
              } />
              <Route path="seg/analysen" element={
                <ProtectedRoute><SegAnalysen /></ProtectedRoute>
              } />
              <Route path="seg/analysen/:propertyId" element={
                <ProtectedRoute><SegAnalysen /></ProtectedRoute>
              } />
              <Route path="bausteine" element={
                <ProtectedRoute roles={["super_admin", "admin", "management", "manager"]}><SnippetsPage /></ProtectedRoute>
              } />
              <Route path="suche" element={<Search />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </PortalGuard>
      </BrowserRouter>
      <PreferencesAwareToaster />
      </ConfirmProvider>
    </AuthProvider>
  );
}

function PreferencesAwareToaster() {
  const [pos, setPos] = useState("bottom-right");
  const [duration, setDuration] = useState(4000);
  useEffect(() => {
    const apply = () => {
      const root = document.documentElement;
      setPos(root.dataset.toastPosition || "bottom-right");
      try {
        const raw = localStorage.getItem("domivus_user_preferences");
        if (raw) {
          const p = JSON.parse(raw);
          if (p.toast_duration) setDuration(p.toast_duration);
        }
      } catch (_) { /* ignore */ }
    };
    apply();
    const obs = new MutationObserver(apply);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["data-toast-position"] });
    window.addEventListener("storage", apply);
    return () => { obs.disconnect(); window.removeEventListener("storage", apply); };
  }, []);
  return <><Toaster position={pos} richColors duration={duration} /><MisdirectPromptHost /></>;
}

export default App;
